import { calcBidPercentage } from '@strata/helper-functions';
import { InlineMessage } from '@strata/react-inline-message';
import { BidInput, TextInputFormik } from '@strata/react-inputs';
import { validationStrings } from '@strata/strata-ui-strings';
import Decimal from 'decimal.js';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import * as Yup from 'yup';

const isRateLessCompetitiveThanDefault = (rate, defaultRate) =>
    (typeof rate === 'string' || typeof rate === 'number') &&
    Number(rate) > calcBidPercentage(defaultRate ?? 1);

export const ActionRateOverride = ({
    label,
    helperText = undefined,
    disabled = false,
    rateRule = undefined,
}) => {
    const formik = useFormikContext();

    const { setFieldValue } = formik;
    useEffect(() => {
        void (async () => {
            if (rateRule?.defaultRate !== formik.values.defaultRate) {
                await setFieldValue('defaultRate', rateRule.defaultRate);
            }
        })();
    }, [rateRule, formik.values, setFieldValue]);

    return (
        <>
            <BidInput
                id="rateOverride"
                label={label}
                helperText={helperText}
                disabled={disabled}
                required={false}
                referentPriceType={rateRule?.referentPriceType}
                minAllowedBid={rateRule?.defaultRate}
            />
            {isRateLessCompetitiveThanDefault(
                formik.values.rateOverride,
                formik.values.defaultRate,
            ) && (
                <>
                    <div className="margin__bottom--small">
                        <InlineMessage
                            intent={InlineMessage.Intent.WARNING}
                            label='This rate is less competitive than the default rate. If intentional, type "CONFIRM" below.'
                            wrapText
                        />
                    </div>
                    <TextInputFormik
                        id="lessCompetitiveRateOverrideConfirmation"
                        label=""
                        required
                        placeholderText="TYPE CONFIRM"
                    />
                </>
            )}
        </>
    );
};

ActionRateOverride.propTypes = {
    label: PropTypes.string.isRequired,
    helperText: PropTypes.node,
    disabled: PropTypes.bool,
    rateRule: PropTypes.shape({
        defaultRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        referentPriceType: PropTypes.string.isRequired,
    }),
};

ActionRateOverride.initialValues = {
    rateOverride: undefined,
    lessCompetitiveRateOverrideConfirmation: undefined,
};

ActionRateOverride.validationSchema = {
    rateOverride: BidInput.validator(),
    lessCompetitiveRateOverrideConfirmation: Yup.string().when(['rateOverride', 'defaultRate'], {
        is: (rateOverride, defaultRate) =>
            isRateLessCompetitiveThanDefault(rateOverride, defaultRate),
        then: (schema) =>
            schema
                .required(validationStrings.requiredField.errorMessage)
                .matches(/CONFIRM/, validationStrings.requiredField.errorMessage),
    }),
};

ActionRateOverride.submitValue = (values) =>
    typeof values.rateOverride === 'number'
        ? Decimal.div(values.rateOverride, 100).toNumber()
        : undefined;
